import {Component, forwardRef} from '@angular/core';
import {CacheDataService} from '../../api/services/cache-data.service';
import {FlatTreeControl} from '@angular/cdk/tree';
import {TreeViewSelectionNode} from './helpers/tree-view-selection-node';
import {TreeViewDataSource} from './helpers/tree-vew-data-source';
import {FilterEditorComponent} from './filter-editor/filter-editor.component';
import {Region} from '../../api/models/region';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {RegionKey} from '../../api/models/region-key';

@Component({
  selector: "region-filter-editor",
  templateUrl: "./filter-editor/filter-editor.component.html",
  styleUrls: ["./filter-editor/filter-editor.component.css"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RegionFilterEditorComponent),
      multi: true
    }
  ]

})
export class RegionFilterEditorComponent extends FilterEditorComponent<Region, RegionKey, TVSNRegion, RegionDataSource> implements ControlValueAccessor {
  constructor(cacheDataService: CacheDataService) {
    super(cacheDataService);

  }

  protected getNewDataSource(treeControl: FlatTreeControl<TVSNRegion, TVSNRegion>, cacheDataService: CacheDataService): RegionDataSource {
      return new RegionDataSource(treeControl, cacheDataService);
    }
}

export class RegionDataSource extends TreeViewDataSource<Region, RegionKey, TVSNRegion> {

  constructor(
    treeControl: FlatTreeControl<TVSNRegion>,
    public cacheDataService: CacheDataService) {
    super(treeControl);
  }

  protected loadInitialData(callback: (results: TVSNRegion[]) => void) {
    this.cacheDataService.cacheDataGetRegionStates({ pageNumber: 1, pageSize: 1000 }).subscribe(result => {
      const nodes = result.page.filter(x => x.stateFullName && x.stateFullName.trim() !== '').map(item => new TVSNRegion(
        [], this.blankCover(item.stateFullName ?? item.state), 1,
        {
          state: item.state,
          stateFullName: item.stateFullName
        },
        undefined, true, true
      ));
      callback(nodes);
    });
  }

  protected loadChildNodes(node: TVSNRegion, callback: (results: TVSNRegion[]) => void) {
    if (node.level === 1) {
      this.cacheDataService.cacheDataGetRegionCountiesByState({ state: node.sourceValue.state, pageNumber: 1, pageSize: 1000 }).subscribe(result => {
        const nodes : TVSNRegion[] = result.page.filter(x => x.county).map(item => new TVSNRegion(
          [], this.blankCover(item.county), node.level + 1,
          {
            state: item.state,
            stateFullName: item.stateFullName,
            county: item.county
          },
          node, true, true
        ));
        callback(nodes);
      });
    } else if (node.level === 2) {
      this.cacheDataService.cacheDataGetRegionZipCitiesByStateAndCount({ state: node.sourceValue.state, county: node.sourceValue.county, pageNumber: 1, pageSize: 1000 }).subscribe(result => {
        const nodes : TVSNRegion[] = result.page.filter(x => x.zipCity).map(item => new TVSNRegion(
          [], item.zipCity, node.level + 1,
          {
            state: item.state,
            stateFullName: item.stateFullName,
            county: item.county,
            cbsaCode: item.cbsaCode,
            cbsaType: item.cbsaType,
            cbsaName: item.cbsaName,
            zipCity: item.zipCity,
          },
          node, false, true
        ));
        callback(nodes);
      });
    }
  }
}

export class TVSNRegion extends TreeViewSelectionNode<Region, RegionKey> {

  protected deriveKey(): RegionKey {
    return {
      state: this.sourceValue.state,
      county: this.sourceValue.county,
      zipCity: this.sourceValue.zipCity
    };
  }

  protected isAnyKnownChildSelected(allKnownSelections: RegionKey[]): boolean {
    if (!this.sourceValue.zipCity) {
      if (this.sourceValue.county) {
        return allKnownSelections?.some(x => x.county == this.sourceValue.county && x.zipCity);
      } else if (this.sourceValue.state) {
        return allKnownSelections?.some(x => x.state === this.sourceValue.state && x.county);
      }
    }
    return false;
  }

  public isKeyForMe(key: RegionKey): boolean {
    return key.state === this.sourceValue.state
      && key.county === this.sourceValue.county
      && key.zipCity === this.sourceValue.zipCity;
  }

  public isChildOf(key: RegionKey): boolean {
    // This node is a child of 'key' if 'key' represents a parent in the hierarchy

    // If key is a county level node, check if this is a zipCity under that county
    if (key.county != null && key.zipCity == null) {
      return this.sourceValue.state === key.state
        && this.sourceValue.county === key.county
        && this.sourceValue.zipCity != null;
    }
    // If key is a state level node, check if this is a county or zipCity under that state
    else if (key.state != null && key.county == null && key.zipCity == null) {
      return this.sourceValue.state === key.state
        && (this.sourceValue.county != null || this.sourceValue.zipCity != null);
    }

    return false;
  }

  public toSourceKey(): RegionKey {
    return {
      state: this.sourceValue.state,
      county: this.sourceValue.county,
      zipCity: this.sourceValue.zipCity
    }
  }

  public matchesKey(key: RegionKey): boolean {
    return key.state === this.sourceValue.state
      && key.county === this.sourceValue.county
      && key.zipCity === this.sourceValue.zipCity;
  }

}
